
import React from "react";

import {
    Users,
    BedDouble,
    Maximize2,
    Clock3,
    Eye,
    Star,
    Wifi,
    Wind,
    Coffee,
    CheckCircle2,
    Bath,
    ChevronRight,
} from "lucide-react";

/* =========================================================
   AMENITY ICON
========================================================= */

const getAmenityIcon = (amenity) => {
    const value = String(amenity || "").toLowerCase();

    if (
        value.includes("wi-fi") ||
        value.includes("wifi")
    ) {
        return (
            <Wifi
                size={15}
                strokeWidth={1.8}
            />
        );
    }

    if (
        value === "ac" ||
        value.includes("air")
    ) {
        return (
            <Wind
                size={15}
                strokeWidth={1.8}
            />
        );
    }

    if (
        value.includes("tea") ||
        value.includes("coffee")
    ) {
        return (
            <Coffee
                size={15}
                strokeWidth={1.8}
            />
        );
    }

    if (
        value.includes("bath") ||
        value.includes("shower")
    ) {
        return (
            <Bath
                size={15}
                strokeWidth={1.8}
            />
        );
    }

    return (
        <CheckCircle2
            size={15}
            strokeWidth={1.8}
        />
    );
};

/* =========================================================
   CHECK OFFER VALIDITY
========================================================= */

const isOfferValid = (offer) => {
    if (!offer) return false;

    if (!offer.isActive) {
        return false;
    }

    if (offer.offerType !== "RESERVATION") {
        return false;
    }

    if (
        Array.isArray(offer.services) &&
        offer.services.length > 0 &&
        !offer.services.includes("RESERVATION")
    ) {
        return false;
    }

    const now = new Date();

    const validFrom = offer.validFrom
        ? new Date(offer.validFrom)
        : null;

    const validTo = offer.validTo
        ? new Date(offer.validTo)
        : null;

    if (validFrom && now < validFrom) {
        return false;
    }

    if (validTo && now > validTo) {
        return false;
    }

    if (
        offer.usageLimit !== null &&
        offer.usageLimit !== undefined &&
        Number(offer.usedCount || 0) >=
            Number(offer.usageLimit)
    ) {
        return false;
    }

    return true;
};

/* =========================================================
   CALCULATE DISCOUNT
========================================================= */

const calculateDiscount = (
    price,
    offer
) => {
    if (!offer || price <= 0) {
        return {
            discountAmount: 0,
            finalPrice: price,
        };
    }

    if (
        Number(
            offer.minimumBookingAmount || 0
        ) > price
    ) {
        return {
            discountAmount: 0,
            finalPrice: price,
        };
    }

    let discountAmount = 0;

    if (
        offer.discountType === "PERCENTAGE"
    ) {
        discountAmount =
            (
                price *
                Number(
                    offer.discountValue || 0
                )
            ) / 100;

        if (
            offer.maxDiscountAmount &&
            discountAmount >
                Number(
                    offer.maxDiscountAmount
                )
        ) {
            discountAmount =
                Number(
                    offer.maxDiscountAmount
                );
        }
    }

    if (
        offer.discountType === "FIXED"
    ) {
        discountAmount =
            Number(
                offer.discountValue || 0
            );
    }

    discountAmount = Math.min(
        discountAmount,
        price
    );

    return {
        discountAmount,
        finalPrice:
            price - discountAmount,
    };
};

/* =========================================================
   ROOM CARD
========================================================= */

const RoomCard = ({
    room,
    onBook,
    offers = [],
}) => {

    /* =====================================================
       API DATA FALLBACK

       Your current room object contains:
       
       room.apiData

       So we use apiData as a fallback for fields that
       were lost during the room transformation.
    ===================================================== */

    const apiData =
        room?.apiData || {};

    /* =====================================================
       ORIGINAL ROOM PRICE
    ===================================================== */

const price = Number(
    room?.channelManagerRate ??
    room?.todayPrice ??
    room?.price ??
    apiData?.channelManagerRate ??
    apiData?.todayPrice ??
    apiData?.price ??
    room?.basePrice ??
    apiData?.basePrice ??
    0
);

    /* =====================================================
       GET VALID RESERVATION OFFERS
    ===================================================== */

    const applicableOffers =
        Array.isArray(offers)
            ? offers.filter(isOfferValid)
            : [];

    const selectedOffer =
        applicableOffers[0] || null;

    /* =====================================================
       CALCULATE DISCOUNT
    ===================================================== */

    const {
        discountAmount,
        finalPrice,
    } = calculateDiscount(
        price,
        selectedOffer
    );

    const hasDiscount =
        Boolean(
            selectedOffer &&
            discountAmount > 0
        );

    /* =====================================================
       ROOM DATA
    ===================================================== */

    const rating = Number(
        room?.rating ??
        apiData?.rating ??
        0
    );

    const reviewCount = Number(
        room?.reviews ??
        apiData?.reviews ??
        0
    );

    const availableCount = Number(
        room?.availableRooms ??
        room?.inventoryAvailable ??
        apiData?.availableRooms ??
        apiData?.inventoryAvailable ??
        room?.inventory ??
        apiData?.inventory ??
        0
    );

    const roomDescription =
        room?.description ||
        apiData?.description ||
        "Comfortable and beautifully designed accommodation for a relaxing stay.";

    /* =====================================================
       ROOM POLICY DATA

       IMPORTANT:
       First check room.
       Then fallback to apiData.
    ===================================================== */

    const breakfastIncluded =
        room?.breakfastIncluded ??
        apiData?.breakfastIncluded ??
        false;

    const breakfastType =
        room?.breakfastType ??
        apiData?.breakfastType ??
        "";

    const cancellationPolicy =
        room?.cancellationPolicy ??
        apiData?.cancellationPolicy ??
        "";

    const freeCancellation =
        room?.freeCancellation ??
        apiData?.freeCancellation ??
        false;

    const freeCancellationHours =
        room?.freeCancellationHours ??
        apiData?.freeCancellationHours ??
        null;

    const paymentPolicy =
        room?.paymentPolicy ??
        apiData?.paymentPolicy ??
        "";

    const termsAndConditions =
        room?.termsAndConditions ??
        apiData?.termsAndConditions ??
        "";

    /* =====================================================
       BOOKING ROOM OBJECT

       We merge apiData + room.

       room is spread AFTER apiData so the normalized
       room values remain the primary values.

       Then we explicitly attach booking/policy fields.
    ===================================================== */

    const bookingRoom = {
        ...apiData,
        ...room,

        /* Price information */
        originalPrice: price,
        discountedPrice: finalPrice,
        discountAmount: discountAmount,
        appliedOffer: selectedOffer,

        /* Breakfast */
        breakfastIncluded:
            breakfastIncluded === true,

        breakfastType:
            breakfastType,

        /* Cancellation */
        cancellationPolicy:
            cancellationPolicy,

        freeCancellation:
            freeCancellation === true,

        freeCancellationHours:
            freeCancellationHours,

        /* Payment */
        paymentPolicy:
            paymentPolicy,

        /* Terms */
        termsAndConditions:
            termsAndConditions,
    };

    /* =====================================================
       DEBUG

       Remove after confirming everything works.
    ===================================================== */

    console.log(
        "ROOM CARD FINAL BOOKING DATA:",
        bookingRoom
    );

    return (
        <article className="be-room-card-new">

            {/* =================================================
                IMAGE
            ================================================= */}

            <div className="be-room-card-image">

                <img
                    src={
                        room?.images?.[0] ||
                        apiData?.images?.[0] ||
                        room?.image ||
                        "https://images.unsplash.com/photo-1566665797739-1674de7a421a"
                    }
                    alt={
                        room?.name ||
                        apiData?.name ||
                        "Room"
                    }
                    className="be-room-image-new"
                />

                {availableCount > 0 && (
                    <div className="be-room-availability-badge">

                        <CheckCircle2
                            size={13}
                            strokeWidth={1.8}
                        />

                        <span>
                            {availableCount}{" "}
                            {availableCount === 1
                                ? "room available"
                                : "rooms available"}
                        </span>

                    </div>
                )}

            </div>

            {/* =================================================
                ROOM INFORMATION
            ================================================= */}

            <div className="be-room-card-content">

                {/* TITLE */}

                <div className="be-room-card-title-row">

                    <div>

                        <div className="be-room-type-label">
                            {room?.type ||
                                apiData?.type ||
                                room?.category ||
                                apiData?.category ||
                                "Room"}
                        </div>

                        <h2 className="be-room-card-title">
                            {room?.name ||
                                apiData?.name}
                        </h2>

                    </div>

                    {rating > 0 && (
                        <div className="be-room-rating">

                            <Star
                                size={14}
                                strokeWidth={1.8}
                                fill="currentColor"
                            />

                            <strong>
                                {rating.toFixed(1)}
                            </strong>

                            {reviewCount > 0 && (
                                <span>
                                    {reviewCount} reviews
                                </span>
                            )}

                        </div>
                    )}

                </div>

                {/* DESCRIPTION */}

                <p className="be-room-card-description">
                    {roomDescription}
                </p>

                {/* ROOM INFORMATION */}

                <div className="be-room-card-details">

                    {/* GUEST */}

                    <div className="be-room-detail">

                        <Users
                            size={19}
                            strokeWidth={1.7}
                        />

                        <span>
                            {room?.maxAdults ??
                                apiData?.maxAdults ??
                                0}{" "}
                            {Number(
                                room?.maxAdults ??
                                apiData?.maxAdults ??
                                0
                            ) === 1
                                ? "Adult"
                                : "Adults"}

                            {Number(
                                room?.maxChildren ??
                                apiData?.maxChildren ??
                                0
                            ) > 0 && (
                                <>
                                    {" · "}
                                    {room?.maxChildren ??
                                        apiData?.maxChildren}{" "}
                                    {Number(
                                        room?.maxChildren ??
                                        apiData?.maxChildren
                                    ) === 1
                                        ? "Child"
                                        : "Children"}
                                </>
                            )}
                        </span>

                    </div>

                    <span className="be-room-detail-separator">
                        |
                    </span>

                    {/* BED */}

                    {(room?.bedType ||
                        apiData?.bedType) && (
                        <>
                            <div className="be-room-detail">

                                <BedDouble
                                    size={19}
                                    strokeWidth={1.7}
                                />

                                <span>
                                    {room?.bedType ||
                                        apiData?.bedType}
                                </span>

                            </div>

                            <span className="be-room-detail-separator">
                                |
                            </span>
                        </>
                    )}

                    {/* SIZE */}

                    {Number(
                        room?.roomSize ??
                        apiData?.roomSize ??
                        0
                    ) > 0 && (
                        <>
                            <div className="be-room-detail">

                                <Maximize2
                                    size={18}
                                    strokeWidth={1.7}
                                />

                                <span>
                                    {room?.roomSize ??
                                        apiData?.roomSize}{" "}
                                    {room?.roomSizeUnit ||
                                        apiData?.roomSizeUnit ||
                                        "sqft"}
                                </span>

                            </div>

                            <span className="be-room-detail-separator">
                                |
                            </span>
                        </>
                    )}

                    {/* VIEW */}

                    {(room?.viewType ||
                        apiData?.viewType) && (
                        <div className="be-room-detail">

                            <Eye
                                size={19}
                                strokeWidth={1.7}
                            />

                            <span>
                                {room?.viewType ||
                                    apiData?.viewType}
                            </span>

                        </div>
                    )}

                </div>

                {/* CHECK IN / CHECK OUT */}

                <div className="be-room-timing">

                    <div className="be-room-time-button">

                        <Clock3
                            size={15}
                            strokeWidth={1.8}
                        />

                        <span>
                            Check-in
                        </span>

                        <strong>
                            {room?.checkInTime ||
                                apiData?.checkInTime ||
                                "--"}
                        </strong>

                    </div>

                    <div className="be-room-time-button">

                        <Clock3
                            size={15}
                            strokeWidth={1.8}
                        />

                        <span>
                            Check-out
                        </span>

                        <strong>
                            {room?.checkOutTime ||
                                apiData?.checkOutTime ||
                                "--"}
                        </strong>

                    </div>

                </div>

                {/* AMENITIES */}

                {Array.isArray(
                    room?.amenities ||
                    apiData?.amenities
                ) &&
                    (
                        room?.amenities ||
                        apiData?.amenities
                    ).length > 0 && (

                        <div className="be-amen-type-button">

                            {(
                                room?.amenities ||
                                apiData?.amenities ||
                                []
                            )
                                .slice(0, 5)
                                .map((item) => (

                                    <span
                                        key={item}
                                        className="be-room-amenity"
                                    >

                                        {getAmenityIcon(item)}

                                        <span>
                                            {item}
                                        </span>

                                    </span>

                                ))}

                            {(
                                room?.amenities ||
                                apiData?.amenities ||
                                []
                            ).length > 5 && (

                                <span className="be-room-more-amenities">
                                    +
                                    {(
                                        room?.amenities ||
                                        apiData?.amenities ||
                                        []
                                    ).length - 5}{" "}
                                    more
                                </span>

                            )}

                        </div>
                    )}

            </div>

            {/* =================================================
                PRICE / BOOKING
            ================================================= */}

            <div className="be-room-card-price">

                <div className="be-price-label">
                    Starting from
                </div>

                {/* ORIGINAL PRICE */}

                {hasDiscount && (
                    <div className="be-original-room-price">
                        ₹{price.toLocaleString("en-IN")}
                    </div>
                )}

                {/* OFFER PRICE */}

                <div
                    className={
                        hasDiscount
                            ? "be-room-price be-offer-price"
                            : "be-room-price"
                    }
                >
                    ₹{finalPrice.toLocaleString("en-IN")}
                </div>

                <div className="be-price-night">
                    Per night
                </div>

                <div className="be-price-tax">
                    + taxes & fees
                </div>

                {/* BOOK NOW */}

                <button
                    type="button"
                    className="book-now-btn"
                    onClick={(e) => {

                        e.preventDefault();
                        e.stopPropagation();

                        if (
                            typeof onBook ===
                            "function"
                        ) {
                            onBook(
                                bookingRoom
                            );
                        }

                    }}
                >

                    <span>
                        Book Now
                    </span>

                    <ChevronRight
                        size={16}
                        strokeWidth={1.9}
                    />

                </button>

                {/* EXTRA BED */}

                {(room?.extraBedAllowed ??
                    apiData?.extraBedAllowed) && (

                    <div className="be-extra-bed-note">

                        <CheckCircle2
                            size={13}
                            strokeWidth={1.8}
                        />

                        Extra bed available

                    </div>
                )}

            </div>

        </article>
    );
};

export default RoomCard;
